import { useToast } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { SpanStatusType } from "@sentry/tracing";
import {
  ReportPeriodType,
  buildReportShortName,
} from "@syla/shared/helpers/buildReportFileName";
import React, { useState } from "react";
import { errorToToastOptions } from "../../../api/errors/errorToToastOptions";
import { downloadSupportCsv } from "../../../api/user/downloadSupportCsv";
import {
  SupportCsvDocument,
  UserRemainingParcels,
} from "../../../constants/SupportCsvDocument";
import reportCsv from "../../../images/icons/reportCsv.svg";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { useGlobalVariablesStore } from "../../../store/globalVariablesStore";
import { errorToastOptions } from "../../molecules/errorToastOptions";
import { DocumentRow } from "./DocumentRow";

interface SupportDocumentRowProps {
  isLoading: boolean;
  canDownload: boolean;
  csvDocument: SupportCsvDocument;
  canPurchase: boolean;
  sampleUrl: string;
}

export const SupportDocumentRow = ({
  isLoading,
  canDownload,
  canPurchase,
  csvDocument,
  sampleUrl,
}: SupportDocumentRowProps): JSX.Element => {
  const toast = useToast();

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const selectedFinancialYear = useGlobalVariablesStore(
    (state) => state.selectedFinancialYear,
  );

  const [downloading, setDownloading] = useState(false);

  // handle download click
  const handleDownload = async () => {
    if (!selectedFinancialYear) {
      toast({
        ...errorToastOptions,
        title: "Financial year not selected",
      });

      return;
    }

    // instrument
    const transaction = Sentry.startTransaction({
      name: `/tax-reports/${csvDocument.description}`,
      data: { financialYearValue: selectedFinancialYear.value },
    });

    const span = transaction.startChild({
      op: "task",
      description: csvDocument.description,
    });

    try {
      setDownloading(true);
      // Set transaction on scope to associate with errors and get included span instrumentation
      Sentry.getCurrentHub().configureScope((scope) =>
        scope.setSpan(transaction),
      );
      await downloadSupportCsv({
        accountId,
        financialYearValue: selectedFinancialYear.value,
        csvDocument,
      });
      span.setStatus("ok" as SpanStatusType);
    } catch (error: any) {
      span.setStatus("unknown_error" as SpanStatusType);

      toast({
        ...errorToToastOptions(error),
        title: `File download failed`,
      });
    }

    span.finish();
    transaction.finish();

    setDownloading(false);
  };

  const reportName =
    !selectedFinancialYear ? ""
    : csvDocument === UserRemainingParcels ?
      buildReportShortName({
        period: {
          date: selectedFinancialYear?.endDate,
          timezone: "Australia/Brisbane",
          type: ReportPeriodType.PointInTime,
        },
        name: csvDocument.name,
      })
    : buildReportShortName({
        period: {
          start: selectedFinancialYear?.startDate,
          end: selectedFinancialYear?.endDate,
          timezone: "Australia/Brisbane",
          type: ReportPeriodType.FinancialYear,
        },
        name: csvDocument.name,
      });

  return (
    <DocumentRow
      isLoading={isLoading}
      downloading={downloading}
      canDownload={canDownload}
      canPurchase={canPurchase}
      image={reportCsv}
      sampleUrl={sampleUrl}
      documentName={reportName}
      handleDownload={handleDownload}
    />
  );
};
